import { memo } from "react";
import { NumericFormat } from "react-number-format";

import styles from "./currency-field-component.module.scss";
import {
  DEFAULT_CURRENCY_SYMBOL,
  PAYMENT_MAX_AMOUNT,
} from "@zenith/data/constants";

/**
 * Props for Currency Field Component.
 * @interface
 * @name CurrencyFieldComponentProps
 * @kind interface
 */
interface CurrencyFieldComponentProps {
  /**
   * Id for Currency Field.
   * @property
   * @name id
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {string}
   */
  id: string;
  /**
   * Value for Currency Field.
   * @property
   * @name value
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {string | number | null | undefined}
   */
  value: string | number | null | undefined;
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {string | undefined}
   */
  className?: string;
  /**
   * (optional)
   * Standard HTML Attribute.
   * @property
   * @name disabled
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {boolean | undefined}
   */
  disabled?: boolean;
  /**
   * (optional)
   * The error message.
   * @property
   * @name errorMessage
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {string | undefined}
   */
  errorMessage?: string;
  /**
   * (optional)
   * If `true`, the error message is shown.
   * @property
   * @name hasError
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {boolean | undefined}
   */
  hasError?: boolean;
  /**
   * (optional)
   * The `label` text.
   * @property
   * @name label
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {string | undefined}
   */
  label?: string;
  /**
   * (optional)
   * Standard Focus Event Attribute.
   * @property
   * @name onBlur
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {React.FocusEventHandler<HTMLInputElement> | undefined}
   */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /**
   * (optional)
   * Standard DOM Attribute Change Event.
   * @property
   * @name onChange
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {React.ChangeEventHandler<HTMLInputElement> | undefined}
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /**
   * (optional)
   * Standard DOM Attribute Keyboard Event.
   * @property
   * @name onKeyDown
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {React.KeyboardEventHandler<HTMLInputElement> | undefined}
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /**
   * (optional)
   * Function to determine if the input value is allowed.
   * @property
   * @name isAllowed
   * @kind property
   * @memberof CurrencyFieldComponentProps
   * @type {React.ComponentProps<typeof NumericFormat>["isAllowed"] | undefined}
   */
  isAllowed?: React.ComponentProps<typeof NumericFormat>["isAllowed"];
}

/**
 * Memoized Currency Field Component.
 * @constant
 * @name CurrencyFieldComponent
 * @kind variable
 * @type {React.NamedExoticComponent<CurrencyFieldComponentProps>}
 */
const CurrencyFieldComponent: React.NamedExoticComponent<CurrencyFieldComponentProps> =
  memo(
    /**
     * Currency Field Component.
     * @function
     * @name CurrencyFieldComponent
     * @kind function
     * @memberof CurrencyFieldComponent
     * @param {CurrencyFieldComponentProps} props Props for Currency Field Component.
     * @returns {JSX.Element} Currency Field Component.
     */
    function CurrencyFieldComponent(
      props: CurrencyFieldComponentProps
    ): JSX.Element {
      const {
        id,
        value,
        className,
        disabled,
        errorMessage,
        hasError,
        label,
        onBlur,
        onChange,
        onKeyDown,
        isAllowed,
      } = props;

      const isAllowedCallback =
        isAllowed ??
        ((values) => {
          const totalCharacters = values.value.replace(/,/g, "").length;
          const [, decimalPart] = values.value.split(".");

          // Check if the total number of characters exceeds the limit
          if (totalCharacters > 13) {
            return false;
          }

          // Check if the decimal part exceeds 2 digits
          if (decimalPart && decimalPart.length > 2) {
            return false;
          }

          // Check if the numeric value exceeds the maximum allowed amount
          const numericValue = Number(values.value.replace(/,/g, ""));
          if (numericValue > PAYMENT_MAX_AMOUNT) {
            return false;
          }

          return true;
        });

      return (
        <label className="flex flex-col text-sm font-normal leading-loose text-neutral-500">
          {label}
          <div className={styles.currencyWrapper}>
            <NumericFormat
              allowNegative={false}
              placeholder="0.00"
              value={value}
              id={id}
              className={`mb-2 rounded-1.5lg border ${
                hasError ? "border-primary-250" : "border-neutral-200"
              } px-4 py-3 pl-11 text-lg leading-normal text-black focus:outline-neutral-900 ${className}`}
              decimalScale={2}
              fixedDecimalScale
              onChange={onChange}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              disabled={disabled}
              allowLeadingZeros={false}
              thousandSeparator={true}
              isAllowed={isAllowedCallback}
            />
          </div>
          <p
            className={`flex text-xs font-normal text-primary-250 ${
              !hasError ? "mb-4" : ""
            }`}
          >
            {hasError && errorMessage}
          </p>
        </label>
      );
    }
  );

export default CurrencyFieldComponent;
